/** XeAgents.ai**/
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { ArrowRight, Gift, Headphones } from "lucide-react";
import { useAuthDialog } from "@/components/landing/AuthDialog";
import { scrollToSection } from "@/lib/section-nav";
import { FREE_VOICE_CREDITS } from "@/lib/free-credits";

/**
 * The strip above the navbar. It is part of the fixed header rather than a
 * sibling of it, so the two scroll as one block and anchor offsets can be
 * measured from a single element.
 */
export function AnnouncementBar() {
  const { t } = useTranslation("landing");
  const [location, setLocation] = useLocation();
  const { openAuth } = useAuthDialog();

  /**
   * Hands off to the floating live-call widget at the bottom of the page.
   * "open-live-call" is the widget's own trigger - SignupPopup already uses it -
   * so this starts exactly the call its button would.
   */
  const handleTryCallingAgent = () => {
    if (location !== "/") {
      setLocation("/");
    }
    window.dispatchEvent(new CustomEvent("open-live-call"));
  };

  // Support is the contact section, not /help - that route is behind auth.
  const handleSupport = () => {
    if (location !== "/") {
      setLocation("/#contact");
      return;
    }
    scrollToSection("#contact");
  };

  return (
    <div
      className="w-full bg-gradient-to-r from-fuchsia-600 via-violet-600 to-purple-700 text-white"
      data-testid="announcement-bar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-x-4 px-4 py-2 sm:px-6 lg:justify-between lg:px-8">
        <button
          type="button"
          onClick={() => openAuth("register")}
          className="group flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs font-medium sm:text-sm"
          data-testid="button-announcement-credits"
        >
          <Gift className="h-4 w-4 flex-shrink-0 text-amber-300" aria-hidden="true" />
          <span className="font-semibold">{t("credits.get", { count: FREE_VOICE_CREDITS })}</span>
          <span className="hidden text-white/60 sm:inline" aria-hidden="true">
            &middot;
          </span>
          <span className="hidden text-white/85 sm:inline">{t("credits.noCard")}</span>
          <span className="hidden text-white/60 sm:inline" aria-hidden="true">
            &middot;
          </span>
          <span className="inline-flex items-center gap-1 text-white/85 underline-offset-4 group-hover:underline">
            {t("announce.startCall")}
            <ArrowRight
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </span>
        </button>

        <div className="hidden items-center gap-5 text-sm lg:flex">
          <button
            type="button"
            onClick={handleTryCallingAgent}
            className="group inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 font-medium text-white ring-1 ring-white/25 transition-colors hover:bg-white/25"
            data-testid="button-announcement-try-agent"
          >
            <Headphones className="h-3.5 w-3.5" aria-hidden="true" />
            {t("announce.tryAgent")}
          </button>
          <button
            type="button"
            onClick={handleSupport}
            className="text-white/85 transition-colors hover:text-white"
            data-testid="button-announcement-support"
          >
            {t("announce.support")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnnouncementBar;
